import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const DynamicBlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`${API}/api/seo/public/articles/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(setArticle)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-[#09090b]">
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-[#f5f5f5] mb-4">Article introuvable</h1>
          <p className="text-[#71717a] mb-8">Cet article n'existe pas ou n'est pas encore publie.</p>
          <Link to="/blog" className="text-[#3b82f6] hover:underline">Retour au blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const faqSchema = article.faq?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  } : null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.meta_description,
    "image": article.hero_image || "",
    "datePublished": article.published_at,
    "dateModified": article.updated_at || article.published_at,
    "author": { "@type": "Organization", "name": "Cloneia" },
    "publisher": { "@type": "Organization", "name": "Cloneia", "url": "https://cloneia.fr" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://cloneia.fr/blog/${article.slug}` }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Helmet>
        <title>{article.meta_title || article.title} - Blog Cloneia</title>
        <meta name="description" content={article.meta_description || ''} />
        <link rel="canonical" href={`https://cloneia.fr/blog/${article.slug}`} />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://cloneia.fr/blog/${article.slug}`} />
        {article.hero_image && <meta property="og:image" content={article.hero_image} />}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <Header />

      <article className="max-w-3xl mx-auto px-6 pt-32 pb-20" data-testid="blog-article">
        {/* Breadcrumb */}
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-[#71717a] hover:text-[#a1a1aa] text-sm mb-8 transition-colors" data-testid="blog-breadcrumb">
          <ChevronLeft className="w-4 h-4" /> Retour au blog
        </Link>

        {/* Keyword badge */}
        {article.keyword && (
          <span className="text-[#3b82f6] text-xs font-medium uppercase tracking-wider">{article.keyword}</span>
        )}

        <h1 className="text-3xl sm:text-4xl font-bold text-[#f5f5f5] mt-2 mb-4 leading-tight" data-testid="article-title">
          {article.title}
        </h1>

        {/* Meta info */}
        <div className="flex items-center gap-4 text-[#52525b] text-sm mb-8">
          {article.published_at && (
            <span>{new Date(article.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          )}
          {article.word_count > 0 && <span>{Math.ceil(article.word_count / 200)} min de lecture</span>}
        </div>

        {/* Hero image */}
        {article.hero_image && (
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.hero_image}
              alt={article.hero_image_alt || article.title}
              className="w-full aspect-video object-cover"
            />
            {article.hero_image_credit && (
              <p className="text-[#52525b] text-xs mt-2">Credit : {article.hero_image_credit}</p>
            )}
          </div>
        )}

        {/* Article content */}
        <div
          className="prose prose-invert prose-lg max-w-none
            prose-headings:text-[#f5f5f5] prose-headings:font-semibold
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-[#a1a1aa] prose-p:leading-relaxed prose-p:mb-4
            prose-a:text-[#3b82f6] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-[#f5f5f5]
            prose-ul:text-[#a1a1aa] prose-li:text-[#a1a1aa]
            prose-blockquote:border-[#3b82f6] prose-blockquote:text-[#a1a1aa] prose-blockquote:bg-[#0f0f17] prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:px-4"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        {/* FAQ */}
        {article.faq?.length > 0 && (
          <div className="mt-14 pt-10 border-t border-white/5" data-testid="article-faq">
            <h2 className="text-2xl font-bold text-[#f5f5f5] mb-6">Questions frequentes</h2>
            <div className="space-y-4">
              {article.faq.map((faq, i) => (
                <div key={i} className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
                  <h3 className="text-[#f5f5f5] font-semibold mb-2">{faq.question}</h3>
                  <p className="text-[#a1a1aa] text-sm leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 p-8 rounded-2xl bg-gradient-to-r from-[#3b82f6]/10 to-[#0f0f17] border border-[#3b82f6]/20 text-center" data-testid="article-cta">
          <h3 className="text-xl font-bold text-[#f5f5f5] mb-2">Rendez votre information accessible</h3>
          <p className="text-[#71717a] text-sm mb-5">Decouvrez comment Cloneia peut transformer la gestion des connaissances dans votre entreprise.</p>
          <Link to="/#demo" className="inline-block px-6 py-3 bg-[#3b82f6] hover:bg-[#2563eb] text-white rounded-full font-medium text-sm transition-colors">
            Demander une demo
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default DynamicBlogArticle;
